/**
 * Line math shared by the sale/quote line editors.
 *
 * Kept free of imports so it can be verified outside the browser.
 * Mirrors the formula used when a line is added:
 *   subTotal = price * unidades + comision * unidades - descuentos
 *
 * `comision` is per unit, `descuentos` is applied once per line and `price`
 * is never rewritten: the unit price always comes from the catalog.
 */
export const toAmount = (value) => {
    const parsed = parseFloat(value)
    return Number.isFinite(parsed) ? parsed : 0
}

export const calcLineSubTotal = ({ price, comision, unidades, descuentos } = {}) => {
    const unitPrice = toAmount(price)
    const unitComision = toAmount(comision)
    const units = toAmount(unidades)
    const discount = toAmount(descuentos)

    return {
        price: unitPrice,
        comision: unitComision,
        unidades: units,
        descuentos: discount,
        subTotal: (unitPrice * units + unitComision * units - discount).toFixed(2)
    }
}

/**
 * Replaces one line of the list with its recalculated amounts and leaves every
 * other line untouched, both the array and the rows, so saving an edit can
 * never reach a different line.
 *
 * `extraFields` maps the recalculated amounts onto the row shape of a given
 * list (the service list keeps its units in `quantity`).
 *
 * Returns `null` when the index does not point to a line, so the caller can
 * refuse to save instead of writing somewhere else.
 */
export const updateLineInList = (list, index, amounts, extraFields) => {
    if (!Array.isArray(list)) return null
    if (!Number.isInteger(index) || index < 0 || index >= list.length) return null

    const totals = calcLineSubTotal(amounts)
    const extra = typeof extraFields === 'function' ? extraFields(totals) : extraFields || {}
    const updated = {
        unidades: totals.unidades,
        comision: totals.comision,
        descuentos: totals.descuentos,
        subTotal: totals.subTotal,
        ...extra
    }

    return list.map((line, position) => (position === index ? { ...line, ...updated } : line))
}
